import React, { useState } from 'react';
import api from '../services/api';

const EDU_GRADES = {
  children: ['幼稚園低班', '幼稚園高班', '小一', '小二', '小三', '小四', '小五', '小六'],
  teenage:  ['中一', '中二', '中三', '中四', '中五', '中六'],
};

const EMPTY = {
  chi_name: '',
  eng_name: '',
  sex_name: '',
  date_of_birth: '',
  edu_grade: '',
  school_name: '',
  mobile: '',
  tel_home: '',
  email: '',
  region: '',
  street: '',
  building: '',
  emergency_contact_name: '',
  emergency_contact_tel: '',
  emergency_contact_relation: '',
  needs_medication: false,
  medication_reason: '',
  has_allergy: false,
  allergy_detail: '',
  can_exercise: true,
  health_notes: '',
  photo_consent: false,
  data_use_consent: false,
};

const Field = ({ label, labelEn, children, required }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={{ marginBottom: '4px' }}>
      {labelEn && <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>{labelEn}{required && ' *'}</div>}
      <div style={{ fontSize: '13px', color: '#6b7280' }}>{label}{required && ' *'}</div>
    </div>
    {children}
  </div>
);

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  lineHeight: '1.4',
  boxSizing: 'border-box',
  outline: 'none',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236c757d'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  paddingRight: '28px',
};

const checkboxRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 14px',
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  marginBottom: '10px',
  cursor: 'pointer',
};

export default function PublicMinorMemberRegistration({ type = 'children' }) {
  const isChildren = type === 'children';
  const typeLabel    = isChildren ? '兒童' : '少年';
  const typeLabelEn  = isChildren ? 'Children' : 'Teenage';
  const accentColor  = isChildren ? '#0ea5e9' : '#7c3aed';

  const [formData, setFormData] = useState({ ...EMPTY });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState('');

  const set = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleChange = (e) => {
    const { name, value, type: t, checked } = e.target;
    set(name, t === 'checkbox' ? checked : value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.chi_name.trim())
      return setError('請填寫兒童 / 少年中文姓名 (Child name is required)');
    if (!formData.emergency_contact_name.trim() || !formData.emergency_contact_tel.trim())
      return setError('請填寫緊急聯絡人姓名及電話 (Emergency contact is required)');
    if (!formData.data_use_consent)
      return setError('請同意個人資料使用聲明 (Please agree to the privacy notice)');

    setLoading(true);
    try {
      const payload = {
        ...formData,
        member_type: type,
        date_of_birth: formData.date_of_birth || null,
      };
      // convert empty strings to null
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      await api.post('/api/v1/minor-members/public/register', payload);
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : '登記失敗，請稍後再試。(Registration failed)');
    } finally {
      setLoading(false);
    }
  };

  const sectionTitle = (en, zh) => (
    <div style={{ borderBottom: `2px solid ${accentColor}`, paddingBottom: '6px', marginBottom: '16px', marginTop: '24px' }}>
      <span style={{ fontWeight: 700, fontSize: '15px', color: accentColor }}>{en}</span>
      <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '14px' }}>{zh}</span>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '32px 16px' }}>
      <div style={{ maxWidth: '620px', margin: '0 auto', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

        {/* Header banner */}
        <div style={{ background: accentColor, padding: '28px 32px', color: '#fff' }}>
          <div style={{ fontSize: '13px', opacity: 0.85, marginBottom: '4px' }}>
            CCBC — {typeLabelEn} Ministry Registration
          </div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>
            {typeLabelEn} Registration Form
          </h1>
          <h2 style={{ margin: '4px 0 0', fontSize: '18px', fontWeight: 700, opacity: 0.9 }}>
            {typeLabel}登記表
          </h2>
          <p style={{ margin: '12px 0 0', fontSize: '13px', opacity: 0.8 }}>
            Please fill in the form below. All records will be reviewed by our admin team.<br />
            請填寫以下資料，同工將盡快聯絡您。
          </p>
        </div>

        <div style={{ padding: '28px 32px' }}>

          {/* Success */}
          {success && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '16px', marginBottom: '6px' }}>
                ✓ Registration Received! 登記已收到！
              </div>
              <p style={{ margin: 0, color: '#15803d', fontSize: '14px' }}>
                Thank you for registering. Our team will contact you to confirm the membership.<br />
                感謝您的登記，同工將盡快與您聯絡確認。
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', color: '#dc2626', fontSize: '14px' }}>
              {error}
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit}>

              {sectionTitle("Child's Information", '兒童 / 少年資料')}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="中文姓名" labelEn="Chinese Name" required>
                    <input name="chi_name" value={formData.chi_name} onChange={handleChange}
                      style={inputStyle} placeholder="中文姓名" disabled={loading} required />
                  </Field>
                </div>
                <Field label="英文姓名" labelEn="English Name">
                  <input name="eng_name" value={formData.eng_name} onChange={handleChange}
                    style={inputStyle} placeholder="English name" disabled={loading} />
                </Field>
                <Field label="性別" labelEn="Gender">
                  <select name="sex_name" value={formData.sex_name} onChange={handleChange} style={selectStyle} disabled={loading}>
                    <option value="">—</option>
                    <option value="男">男 Male</option>
                    <option value="女">女 Female</option>
                  </select>
                </Field>
                <Field label="出生日期" labelEn="Date of Birth">
                  <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange}
                    style={inputStyle} disabled={loading} />
                </Field>
                <Field label="就讀級別" labelEn="School Grade">
                  <select name="edu_grade" value={formData.edu_grade} onChange={handleChange} style={selectStyle} disabled={loading}>
                    <option value="">—</option>
                    {EDU_GRADES[type].map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="就讀學校" labelEn="School Name">
                    <input name="school_name" value={formData.school_name} onChange={handleChange}
                      style={inputStyle} placeholder="School name / 學校名稱" disabled={loading} />
                  </Field>
                </div>
              </div>

              {sectionTitle('Contact', '聯絡資料 (家長 / 監護人)')}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <Field label="手提電話" labelEn="Mobile">
                  <input type="tel" name="mobile" value={formData.mobile} onChange={handleChange}
                    style={inputStyle} placeholder="Mobile / 手機" disabled={loading} />
                </Field>
                <Field label="住宅電話" labelEn="Home Tel">
                  <input type="tel" name="tel_home" value={formData.tel_home} onChange={handleChange}
                    style={inputStyle} placeholder="Home tel / 住宅電話" disabled={loading} />
                </Field>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Field label="電郵" labelEn="Email">
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                      style={inputStyle} placeholder="email@example.com" disabled={loading} />
                  </Field>
                </div>
              </div>

              {sectionTitle('Address', '住宅地址')}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <Field label="地區" labelEn="Region">
                  <input name="region" value={formData.region} onChange={handleChange}
                    style={inputStyle} placeholder="區 Region" disabled={loading} />
                </Field>
                <Field label="街道" labelEn="Street">
                  <input name="street" value={formData.street} onChange={handleChange}
                    style={inputStyle} placeholder="街道 Street" disabled={loading} />
                </Field>
                <Field label="大廈 / 屋苑" labelEn="Building">
                  <input name="building" value={formData.building} onChange={handleChange}
                    style={inputStyle} placeholder="大廈 Building" disabled={loading} />
                </Field>
              </div>

              {sectionTitle('Emergency Contact', '緊急聯絡人')}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <Field label="姓名" labelEn="Name" required>
                  <input name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange}
                    style={inputStyle} placeholder="姓名" disabled={loading} />
                </Field>
                <Field label="電話" labelEn="Tel" required>
                  <input type="tel" name="emergency_contact_tel" value={formData.emergency_contact_tel} onChange={handleChange}
                    style={inputStyle} placeholder="電話" disabled={loading} />
                </Field>
                <Field label="關係" labelEn="Relation">
                  <input name="emergency_contact_relation" value={formData.emergency_contact_relation} onChange={handleChange}
                    style={inputStyle} placeholder="父親 / 母親 / 監護人" disabled={loading} />
                </Field>
              </div>

              {sectionTitle('Health Declaration', '健康聲明')}

              <label style={checkboxRowStyle}>
                <input type="checkbox" name="needs_medication" checked={formData.needs_medication} onChange={handleChange} disabled={loading} />
                <span style={{ fontSize: '14px' }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>Requires regular medication</span>
                  <span style={{ color: '#6b7280' }}>需要定時服藥</span>
                </span>
              </label>
              {formData.needs_medication && (
                <div style={{ marginBottom: '10px', marginTop: '-4px' }}>
                  <input name="medication_reason" value={formData.medication_reason} onChange={handleChange}
                    style={inputStyle} placeholder="藥物名稱及服藥時間 / Medication details" disabled={loading} />
                </div>
              )}

              <label style={checkboxRowStyle}>
                <input type="checkbox" name="has_allergy" checked={formData.has_allergy} onChange={handleChange} disabled={loading} />
                <span style={{ fontSize: '14px' }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>Has allergy</span>
                  <span style={{ color: '#6b7280' }}>有過敏症</span>
                </span>
              </label>
              {formData.has_allergy && (
                <div style={{ marginBottom: '10px', marginTop: '-4px' }}>
                  <input name="allergy_detail" value={formData.allergy_detail} onChange={handleChange}
                    style={inputStyle} placeholder="過敏原 / Allergy details" disabled={loading} />
                </div>
              )}

              <label style={checkboxRowStyle}>
                <input type="checkbox" name="can_exercise" checked={formData.can_exercise} onChange={handleChange} disabled={loading} />
                <span style={{ fontSize: '14px' }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>Can participate in physical activities</span>
                  <span style={{ color: '#6b7280' }}>可參加一般體力活動</span>
                </span>
              </label>

              <Field label="其他健康備註 Other health notes">
                <textarea name="health_notes" value={formData.health_notes} onChange={handleChange}
                  style={{ ...inputStyle, resize: 'vertical' }} rows={2} disabled={loading} />
              </Field>

              {sectionTitle('Consent', '同意聲明')}

              <label style={{ ...checkboxRowStyle, background: '#eff6ff', borderColor: '#93c5fd' }}>
                <input type="checkbox" name="photo_consent" checked={formData.photo_consent} onChange={handleChange} disabled={loading} />
                <span style={{ fontSize: '13px' }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>I agree to the use of photographs taken during church activities.</span>
                  <span style={{ color: '#6b7280' }}>本人同意教會在活動中拍攝及使用相片。</span>
                </span>
              </label>

              <label style={{ ...checkboxRowStyle, background: '#eff6ff', borderColor: '#93c5fd' }}>
                <input type="checkbox" name="data_use_consent" checked={formData.data_use_consent} onChange={handleChange} disabled={loading} />
                <span style={{ fontSize: '13px' }}>
                  <span style={{ display: 'block', fontWeight: 600 }}>
                    I agree to the collection and use of personal data for church purposes. <span style={{ color: '#dc2626' }}>*</span>
                  </span>
                  <span style={{ color: '#6b7280' }}>本人同意教會收集及使用個人資料作堂務用途。</span>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '13px',
                  background: loading ? '#9ca3af' : accentColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  marginTop: '8px',
                }}
              >
                {loading ? 'Submitting... 提交中...' : `Submit Registration 提交登記`}
              </button>

            </form>
          )}
        </div>
      </div>
    </div>
  );
}
